// V3.4 — Lightweight realtime SSE hook.
//
// Opens (and auto-reconnects) a connection to
// `/api/platform/realtime/stream` and dispatches events to a single
// callback. Consumers filter by `event.type` themselves so we don't
// pay the cost of multiple EventSource connections.
//
// Reconnection: exponential backoff capped at 30s. The browser closes
// the connection when the tab is hidden for a long time; we re-open
// on `visibilitychange` so events resume the moment paco comes back.
import { useEffect, useRef } from "react";

const STREAM_PATH = "/api/platform/realtime/stream";
const MAX_BACKOFF_MS = 30_000;

export function useRealtimeEvents(onEvent, enabled = true) {
  // Keep the callback in a ref so the EventSource we open in the
  // effect doesn't churn every render.
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    const streamUrl = `${backendBase.replace(/\/$/, "")}${STREAM_PATH}`;

    let es = null;
    let backoff = 1000;
    let reconnectTimer = null;
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      try {
        // EventSource sends cookies for same-origin / preview-domain
        // requests; `withCredentials` keeps it working when the API
        // domain is different from the origin (Kubernetes ingress).
        es = new EventSource(streamUrl, { withCredentials: true });
        es.onopen = () => {
          backoff = 1000; // reset on a clean open
        };
        es.onmessage = (e) => {
          try {
            const parsed = JSON.parse(e.data);
            cbRef.current?.(parsed);
          } catch {
            // ignore non-JSON heartbeats / malformed payloads
          }
        };
        es.onerror = () => {
          // EventSource auto-reconnects on transient drops, but the
          // browser closes the stream after long backgrounding. We
          // manually re-open with a backoff to be safe.
          if (es) {
            try { es.close(); } catch { /* noop */ }
            es = null;
          }
          if (cancelled) return;
          reconnectTimer = setTimeout(open, backoff);
          backoff = Math.min(MAX_BACKOFF_MS, backoff * 2);
        };
      } catch {
        // EventSource unavailable (very old browser) — silently no-op.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !es) open();
    };

    open();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (es) {
        try { es.close(); } catch { /* noop */ }
      }
    };
  }, [enabled]);
}
